import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Copy, Check } from "lucide-react";
import { SupplierQuoteRequestDocument } from "./SupplierQuoteRequestDocument";
import { useReactToPrint } from "react-to-print";
import { toast } from "sonner";
import { Tables } from "@/integrations/supabase/types";
import { PRODUCTION_DOMAIN } from "@/lib/constants";

interface GenerateSupplierQuoteRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quoteRequest: Tables<"supplier_quote_requests"> & {
    leads: Tables<"leads">;
    suppliers: Tables<"suppliers">;
    itinerary_items?: Tables<"itinerary_items"> | null;
    profiles: Tables<"profiles">;
  };
  brandLogo?: string;
}

export function GenerateSupplierQuoteRequestDialog({
  open,
  onOpenChange,
  quoteRequest,
  brandLogo,
}: GenerateSupplierQuoteRequestDialogProps) {
  const [copied, setCopied] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  const shareableUrl = `${PRODUCTION_DOMAIN}/supplier-quote-request/${quoteRequest.share_token}`;

  const handleDownloadPDF = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Supplier-Quote-Request-${quoteRequest.id.slice(0, 8)}`,
  });

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareableUrl);
      setCopied(true);
      toast.success("Shareable link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy link");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Supplier Quote Request Preview</DialogTitle>
        </DialogHeader>

        {/* Preview Area - Scrollable */}
        <div className="flex-1 overflow-y-auto border rounded-lg bg-background">
          <div ref={printRef}>
            <SupplierQuoteRequestDocument 
              quoteRequest={quoteRequest}
              brandLogo={brandLogo}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
          <Button
            onClick={handleDownloadPDF}
            variant="outline"
            className="gap-2 flex-1"
          >
            <Download className="h-4 w-4" />
            Download PDF
          </Button>
          
          <Button
            onClick={handleCopyLink}
            variant="outline"
            className="gap-2 flex-1"
          >
            {copied ? (
              <>
                <Check className="h-4 w-4" />
                Link Copied!
              </>
            ) : (
              <>
                <Copy className="h-4 w-4" />
                Copy Shareable Link
              </>
            )}
          </Button>

          <Button
            onClick={() => onOpenChange(false)}
            className="flex-1"
          >
            Done
          </Button>
        </div>

        {/* Helper Text */}
        <p className="text-xs text-muted-foreground text-center">
          Share the link or PDF with your supplier via email, WhatsApp, or any preferred channel
        </p>
      </DialogContent>
    </Dialog>
  );
}
